// Utility functions for fetching and aggregating data for widgets
import api from '../../../api/axiosInstance';
import config, { ENDPOINTS } from '../../../api/config';
import { formatDateOnly, formatDateTime, formatTimeOnly } from '../../../utils/dateTime';

export const AVERAGE_LAP_MAX_FASTEST_RATIO = 1.2;
export const COMPARISON_LAP_MAX_FASTEST_RATIO = 1.5;

// Cache for session data
let sessionsCache = null;
let sessionDataCache = {};

// Request deduplication: track in-flight requests to prevent concurrent duplicate calls
let sessionsPromise = null;
const sessionDataPromises = {};

// Storage keys for session invalidation (set before reload after corner reassignment)
const SESSION_INVALIDATED_KEY = 'laptura_session_invalidated';
const SESSION_INVALIDATED_TIMESTAMP_KEY = 'laptura_session_invalidated_ts';

// Get the shared cache-bust timestamp (ensures all components use same URL for deduplication)
export const getInvalidationTimestamp = () => {
  let ts = sessionStorage.getItem(SESSION_INVALIDATED_TIMESTAMP_KEY);
  if (ts) {
    return ts;
  }
  
  // Only generate new timestamp if there's an active invalidation
  const invalidatedSession = sessionStorage.getItem(SESSION_INVALIDATED_KEY);
  if (invalidatedSession) {
    ts = String(Date.now());
    sessionStorage.setItem(SESSION_INVALIDATED_TIMESTAMP_KEY, ts);
    return ts;
  }
  
  // No timestamp and no active invalidation - return empty string
  // Caller should check isSessionInvalidated() first
  return '';
};

// Check if a specific session was invalidated (corners were reassigned)
export const isSessionInvalidated = (sessionId) => {
  const invalidatedSession = sessionStorage.getItem(SESSION_INVALIDATED_KEY);
  return invalidatedSession === String(sessionId);
};

// Mark a session as invalidated (call before page reload after corner reassignment)
export const invalidateSession = (sessionId) => {
  sessionStorage.setItem(SESSION_INVALIDATED_KEY, String(sessionId));
  // Set a shared timestamp that all components will use for cache-busting
  sessionStorage.setItem(SESSION_INVALIDATED_TIMESTAMP_KEY, String(Date.now()));
};

// Clear the invalidation flag (call after fresh data is loaded)
// Note: We keep the timestamp for a short period to allow all components to use the same one
export const clearSessionInvalidation = () => {
  const invalidatedSession = sessionStorage.getItem(SESSION_INVALIDATED_KEY);
  if (invalidatedSession) {
    sessionStorage.removeItem(SESSION_INVALIDATED_KEY);
    // Don't clear timestamp immediately - keep it for 30 seconds so all components can use the same one
    // This prevents generating new timestamps if components load at different times
    const timestamp = sessionStorage.getItem(SESSION_INVALIDATED_TIMESTAMP_KEY);
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 30000) {
        // Only clear if timestamp is old (30+ seconds)
        sessionStorage.removeItem(SESSION_INVALIDATED_TIMESTAMP_KEY);
      }
    }
  }
};

// Clear cache for a specific session (useful after corner reassignment)
export const clearSessionCache = (sessionId) => {
  delete sessionDataCache[sessionId];
  delete sessionDataPromises[sessionId];
};

  // Fetch all sessions (cached + deduplicated)
export const fetchSessions = async () => {
  // IMPORTANT: Check for in-flight request FIRST for deduplication
  // This ensures concurrent calls share the same request
  if (sessionsPromise) {
    return sessionsPromise;
  }
  
  // Check if any session was invalidated - if so, clear sessions cache too
  // (session list might have updated metadata)
  const invalidatedSession = sessionStorage.getItem(SESSION_INVALIDATED_KEY);
  if (invalidatedSession && sessionsCache) {
    sessionsCache = null;
  }
  
  // Return cached data if available (in-memory cache)
  if (sessionsCache) {
    return sessionsCache;
  }
  
  // Create new request and store promise
  sessionsPromise = (async () => {
    try {
      // Load the complete catalog in bounded pages. A fixed first page can hide
      // valid older sessions once a user's history exceeds the endpoint limit.
      const pageSize = 100;
      const allSessions = [];
      let offset = 0;
      let totalCount = 0;

      do {
        const params = new URLSearchParams({
          limit: String(pageSize),
          offset: String(offset),
        });
        if (invalidatedSession) {
          params.set('_t', getInvalidationTimestamp());
        }

        const response = await api.get(`${config.apiUrl}${ENDPOINTS.sessions}?${params}`);
        const page = response.data?.sessions || [];
        allSessions.push(...page);
        totalCount = Number(response.data?.total_count ?? allSessions.length);
        offset += page.length;
        if (page.length === 0) break;
      } while (offset < totalCount);

      sessionsCache = allSessions;
      return sessionsCache;
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    } finally {
      // Clear promise after request completes (allows retry on error)
      sessionsPromise = null;
    }
  })();
  
  return sessionsPromise;
};

  // Fetch detailed session data (cached per session + deduplicated)
// opts.lite=true → ?lite=1 (widget-sized payload, no per_lap_telemetry)
export const fetchSessionData = async (sessionId, forceRefresh = false, opts = {}) => {
  const lite = !!opts.lite;
  const cacheKey = lite ? `lite:${sessionId}` : String(sessionId);

  // IMPORTANT: Check for in-flight request FIRST for deduplication
  if (sessionDataPromises[cacheKey]) {
    return sessionDataPromises[cacheKey];
  }
  
  // Check if this specific session was invalidated
  const wasInvalidated = isSessionInvalidated(sessionId);
  
  // If session was invalidated or force refresh, clear its cache (but not in-flight)
  if (wasInvalidated || forceRefresh) {
    delete sessionDataCache[cacheKey];
    if (!lite) delete sessionDataCache[`lite:${sessionId}`];
  }
  
  // Return cached data if available (in-memory cache)
  if (sessionDataCache[cacheKey]) {
    return sessionDataCache[cacheKey];
  }
  
  // Create new request and store promise
  sessionDataPromises[cacheKey] = (async () => {
    try {
      const params = new URLSearchParams();
      if (lite) params.set('lite', '1');
      if (wasInvalidated) params.set('_t', String(getInvalidationTimestamp()));
      const qs = params.toString();
      const url = `${config.apiUrl}${ENDPOINTS.sessionData(sessionId)}${qs ? `?${qs}` : ''}`;
      
      const response = await api.get(url);
      if (response.data) {
        sessionDataCache[cacheKey] = response.data;
        
        // Clear the invalidation flag after successfully fetching fresh data
        if (wasInvalidated) {
          clearSessionInvalidation();
        }
        
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching session data for ${sessionId}:`, error);
      throw error;
    } finally {
      // Clear promise after request completes (allows retry on error)
      delete sessionDataPromises[cacheKey];
    }
  })();
  
  return sessionDataPromises[cacheKey];
};

// Clear cache (useful for refresh or user change)
export const clearCache = () => {
  sessionsCache = null;
  sessionDataCache = {};
  // Also clear in-flight promises to allow fresh requests
  sessionsPromise = null;
  Object.keys(sessionDataPromises).forEach(key => delete sessionDataPromises[key]);
};

// Aggregate quick stats from sessions
export const aggregateQuickStats = (sessions) => {
  if (!sessions || sessions.length === 0) {
    return {
      totalSessions: 0,
      totalLaps: 0,
      bestLapTime: null,
      bestLapTrack: null,
      topSpeed: null,
    };
  }
  
  const totalSessions = sessions.length;
  const totalLaps = sessions.reduce((sum, s) => sum + (s.total_laps || 0), 0);
  // Best lap for the latest track only (avoids kart/short-track times beating road courses).
  const bestLapTrack = sessions[0]?.track_name || null;
  const resolveTrackIdentity = createTrackIdentityResolver(sessions);
  const latestTrackIdentity = resolveTrackIdentity(sessions[0]);
  const trackTimes = sessions
    .filter((s) => resolveTrackIdentity(s) === latestTrackIdentity)
    .map((s) => s.best_lap_time)
    .filter((t) => t && t > 0);
  const bestLapTime = trackTimes.length > 0 ? Math.min(...trackTimes) : null;
  
  // Prefer latest session top speed (same source Quick Stats used before via /data).
  // Avoid max-across-all: older rows can contain bad/outlier values.
  const latestTop = sessions.find((s) => s.top_speed && s.top_speed > 0)?.top_speed ?? null;

  return {
    totalSessions,
    totalLaps,
    bestLapTime,
    bestLapTrack,
    topSpeed: latestTop,
  };
};

// Get latest session
export const getLatestSession = (sessions) => {
  if (!sessions || sessions.length === 0) return null;
  return sessions[0]; // Sessions are already sorted by date descending
};

// Format lap time
export const formatLapTime = (seconds) => {
  if (!seconds || seconds === 0) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return minutes > 0 ? `${minutes}:${secs.padStart(6, '0')}` : `${secs}s`;
};

/** Compact axis tick (fits ~44px Y-axis on mobile widgets). */
export const formatLapTimeShort = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}:${String(secs).padStart(2, '0')}` : `${secs}s`;
};

export const calculateMedian = (values) => {
  const sorted = (values || [])
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** Human track label — bucket upload junk names so charts stay readable. */
export const displayTrackName = (name) => {
  if (!name) return 'Unknown';
  const t = String(name).trim();
  if (/^AUTO_TRACK$/i.test(t)) return 'Unlabeled';
  if (/^\d{6}_\d{1,2}:\d{2}(AM|PM)$/i.test(t)) return 'Unlabeled';
  if (/^\d{6}/.test(t) && t.length > 8) return 'Unlabeled';
  return t;
};

export const getTrackIdentity = (session) => {
  if (session?.track_layout_id) return `layout:${session.track_layout_id}`;
  const name = String(session?.track_name || '').trim().toLowerCase();
  return `name:${name || 'unknown'}`;
};

/**
 * Coalesce older sessions that predate track-layout contributions when their
 * normalized name maps unambiguously to one canonical layout in the catalog.
 */
export const createTrackIdentityResolver = (sessions = []) => {
  const layoutsByName = new Map();
  const layoutsByAlias = new Map();
  sessions.forEach((session) => {
    const name = String(session?.track_name || '').trim().toLowerCase();
    const alias = String(session?.track_alias_key || '').trim().toLowerCase();
    const layout = session?.track_layout_id;
    if (!layout) return;
    if (name) {
      if (!layoutsByName.has(name)) layoutsByName.set(name, new Set());
      layoutsByName.get(name).add(String(layout));
    }
    if (alias) {
      if (!layoutsByAlias.has(alias)) layoutsByAlias.set(alias, new Set());
      layoutsByAlias.get(alias).add(String(layout));
    }
  });

  return (session) => {
    if (session?.track_layout_id) return getTrackIdentity(session);
    const name = String(session?.track_name || '').trim().toLowerCase();
    const alias = String(session?.track_alias_key || '').trim().toLowerCase();
    const aliasLayouts = layoutsByAlias.get(alias);
    if (alias && aliasLayouts?.size === 1) return `layout:${[...aliasLayouts][0]}`;
    const layouts = layoutsByName.get(name);
    if (layouts?.size === 1) return `layout:${[...layouts][0]}`;
    return getTrackIdentity(session);
  };
};

export const getSessionDateLabel = (session, options = {}) => {
  const value = session?.session_time_recorded
    ? session.session_date
    : (session?.uploaded_at || session?.session_date);
  if (!value) return '';
  const resolvedOptions = { separator: ' - ', ...options };
  return formatDateTime(value, session?.session_time_recorded
    ? { preserveWallClock: true, ...resolvedOptions }
    : { includeTimeZone: true, ...resolvedOptions });
};

export const getSessionTimeLabel = (session) => {
  const value = session?.session_time_recorded
    ? session.session_date
    : (session?.uploaded_at || session?.session_date);
  const time = formatTimeOnly(value, {
    preserveWallClock: !!session?.session_time_recorded,
    includeTimeZone: !session?.session_time_recorded,
  });
  return time ? `${session?.session_time_recorded ? 'Logger' : 'Uploaded'} ${time}` : '';
};

export const getSessionTimestampLabel = (session, options = {}) => {
  const value = getSessionDateLabel(session, options);
  return value ? `${session?.session_time_recorded ? 'Logger' : 'Uploaded'} · ${value}` : '';
};

export const getSessionDayLabel = (session, options = {}) => {
  const value = session?.session_time_recorded
    ? session.session_date
    : (session?.uploaded_at || session?.session_date);
  return formatDateOnly(value, options);
};

export const getSessionDayKey = (session) => {
  const value = session?.session_time_recorded
    ? session.session_date
    : (session?.uploaded_at || session?.session_date);
  if (!value) return '';
  if (session?.session_time_recorded) {
    return String(value).slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getSessionCornerCount = (session) => {
  if (session.total_corners != null) return session.total_corners;
  if (session.track_name?.includes('K1')) return 11;
  if (session.track_name?.includes('SPRING')) return 12;
  if (session.track_name?.includes('SILVERSTONE')) return 18;
  return 10;
};

// Get performance trends data (latest track only — comparable lap times)
export const getPerformanceTrends = (sessions) => {
  if (!sessions || sessions.length === 0) return [];
  const trackName = sessions[0]?.track_name;
  const resolveTrackIdentity = createTrackIdentityResolver(sessions);
  const trackIdentity = resolveTrackIdentity(sessions[0]);
  if (!trackName) return [];

  return sessions
    .filter(
      (s) =>
        resolveTrackIdentity(s) === trackIdentity &&
        s.best_lap_time &&
        s.best_lap_time > 0 &&
        s.session_date
    )
    .map((s) => ({
      date: new Date(s.session_date),
      lapTime: s.best_lap_time,
      trackName: s.track_name,
      dateLabel: getSessionTimestampLabel(s),
    }))
    .sort((a, b) => a.date - b.date);
};

// Get track performance data
export const getTrackPerformance = (sessions) => {
  if (!sessions || sessions.length === 0) return [];

  const resolveTrackIdentity = createTrackIdentityResolver(sessions);
  const trackMap = {};
  sessions.forEach(session => {
    if (session.track_name && session.best_lap_time && session.best_lap_time > 0) {
      const identity = resolveTrackIdentity(session);
      if (!trackMap[identity]) {
        trackMap[identity] = {
          trackName: session.track_name,
          bestLapTime: session.best_lap_time,
          sessionDate: session.session_date,
          sessionCount: 1,
        };
      } else {
        trackMap[identity].sessionCount += 1;
        trackMap[identity].bestLapTime = Math.min(trackMap[identity].bestLapTime, session.best_lap_time);
        if (new Date(session.session_date) > new Date(trackMap[identity].sessionDate)) {
          trackMap[identity].sessionDate = session.session_date;
          trackMap[identity].trackName = session.track_name;
        }
      }
    }
  });
  
  // Most recently driven first — "5 fastest" surfaced only short kart tracks.
  return Object.values(trackMap).sort((a, b) => new Date(b.sessionDate) - new Date(a.sessionDate));
};

// Get sessions grouped by date
export const getSessionsByDate = (sessions, limit = 10) => {
  if (!sessions || sessions.length === 0) return [];
  
  const grouped = {};
  sessions.slice(0, limit).forEach(session => {
    if (session.session_date) {
      const date = getSessionDayLabel(session, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(session);
    }
  });
  
  return Object.entries(grouped).map(([date, sessions]) => ({
    date,
    sessions,
  }));
};

// Filter out excluded laps (first and last laps are typically excluded)
export const filterExcludedLaps = (laps) => {
  if (!laps || !Array.isArray(laps)) return [];
  return laps.filter(lap => !lap.excluded && Number.isFinite(Number(lap.lap_time)) && Number(lap.lap_time) > 0);
};

export const filterRepresentativeLaps = (
  laps,
  maxFastestRatio = AVERAGE_LAP_MAX_FASTEST_RATIO,
) => {
  const valid = filterExcludedLaps(laps);
  if (valid.length < 3) return valid;
  const fastest = Math.min(...valid.map((lap) => Number(lap.lap_time)));
  return valid.filter((lap) => Number(lap.lap_time) <= fastest * maxFastestRatio);
};

export const getCornersForLap = (cornerMetrics, lapNumber) => {
  if (!Array.isArray(cornerMetrics)) return [];
  return cornerMetrics.find((corners) =>
    Array.isArray(corners) && corners.some((corner) => Number(corner?.raw?.lap_number) === Number(lapNumber))
  ) || cornerMetrics[Math.max(0, Number(lapNumber) - 1)] || [];
};
